import { type ProductImageRow, queryKeys } from '@mevaser/shared'
import {
  Badge,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
} from '@mevaser/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Images } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { InfiniteScrollFooter } from './data-table.js'

const PAGE_SIZE = 48

/** Trails `value` by `ms`, so typing a search doesn't fire a request per key. */
export function useDebouncedValue<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return settled
}

export function useImageLibrary(q: string) {
  const filters = { q: q || undefined, pageSize: PAGE_SIZE }
  const query = useInfiniteQuery({
    queryKey: queryKeys.imageLibrary.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: ProductImageRow[]; total: number; page: number; pageSize: number }>(
        '/products/images',
        { query: { ...filters, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  return {
    ...query,
    items: query.data?.pages.flatMap((p) => p.items) ?? [],
    total: query.data?.pages[0]?.total ?? 0,
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function usageLabel(image: ProductImageRow): string {
  return image.usedBy.map((p) => `${p.name} (${p.sku})`).join('\n')
}

/**
 * The library as a thumbnail grid with the same infinite-scroll footer the
 * tables use. Shared by the Image library page and the pick-from-library
 * dialog; `onSelect` makes a card clickable, `renderActions` adds per-card
 * buttons (delete, on the page). `onToggleSelect` adds a checkbox to each card
 * for multi-select (the page only; the dialog never passes it).
 */
export function ImageLibraryGrid({
  q,
  onSelect,
  renderActions,
  selectedIds,
  onToggleSelect,
  isSelectable,
}: {
  q: string
  onSelect?: (image: ProductImageRow) => void
  renderActions?: (image: ProductImageRow) => ReactNode
  selectedIds?: ReadonlySet<string>
  onToggleSelect?: (image: ProductImageRow) => void
  isSelectable?: (image: ProductImageRow) => boolean
}) {
  const { items, total, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useImageLibrary(q)
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage])

  if (isLoading) {
    return <p className="py-8 text-center text-sm text-foreground-muted">Loading images…</p>
  }
  if (isError) {
    return <p className="py-8 text-center text-sm text-danger">Could not load the image library.</p>
  }
  if (items.length === 0) {
    return (
      <EmptyState
        icon={Images}
        title={q ? 'No images match that search' : 'No images yet'}
        description={
          q
            ? 'Try part of the file name instead.'
            : 'Every picture uploaded to a product is saved here automatically.'
        }
      />
    )
  }

  return (
    <div>
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-3 p-3">
        {items.map((image) => {
          const checked = selectedIds?.has(image.id) ?? false
          const selectable = isSelectable ? isSelectable(image) : true
          return (
            <li
              key={image.id}
              className={`relative overflow-hidden rounded-lg border bg-surface ${
                checked ? 'border-primary ring-2 ring-primary' : 'border-border'
              }`}
            >
              {onToggleSelect && (
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!selectable}
                  onChange={() => onToggleSelect(image)}
                  aria-label={`Select ${image.filename ?? 'image'}`}
                  title={
                    selectable ? undefined : 'In use — change the image on those products first'
                  }
                  className="absolute left-2 top-2 z-10 size-4 rounded bg-white accent-[var(--primary)]"
                />
              )}
              <button
                type="button"
                disabled={!onSelect && !(onToggleSelect && selectable)}
                onClick={() => (onSelect ? onSelect(image) : onToggleSelect?.(image))}
                className="block w-full text-left enabled:cursor-pointer enabled:hover:opacity-90"
                aria-label={onSelect ? `Use ${image.filename ?? 'this image'}` : undefined}
              >
                <img
                  src={image.url}
                  alt={image.filename ?? ''}
                  loading="lazy"
                  className="aspect-square w-full bg-surface-sunken object-cover"
                />
              </button>
              <div className="space-y-1 p-2">
                <p className="truncate text-sm font-medium" title={image.filename ?? undefined}>
                  {image.filename ?? 'Untitled'}
                </p>
                <p className="text-xs text-foreground-muted tabular">
                  {formatSize(image.sizeBytes)} · {new Date(image.createdAt).toLocaleDateString()}
                </p>
                <div className="flex items-center justify-between gap-2">
                  {image.usedBy.length > 0 ? (
                    <span title={usageLabel(image)}>
                      <Badge>Used by {image.usedBy.length}</Badge>
                    </span>
                  ) : (
                    <span className="text-xs text-foreground-muted">Unused</span>
                  )}
                  {renderActions?.(image)}
                </div>
              </div>
            </li>
          )
        })}
      </ul>
      <InfiniteScrollFooter
        total={total}
        loaded={items.length}
        hasMore={!!hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={loadMore}
      />
    </div>
  )
}

/** "Choose from library" for a product's image field. */
export function ImageLibraryDialog({
  open,
  onOpenChange,
  onSelect,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (url: string) => void
}) {
  const [search, setSearch] = useState('')
  const q = useDebouncedValue(search.trim(), 250)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Choose from the image library</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by file name"
            aria-label="Search the image library"
          />
          <div className="mt-3 max-h-[60vh] overflow-y-auto">
            <ImageLibraryGrid
              q={q}
              onSelect={(image) => {
                onSelect(image.url)
                onOpenChange(false)
              }}
            />
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
